<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <img src="/logo.png" alt="Happy Alien" class="w-20 h-20 object-contain" />
        </div>
        <h2 class="text-3xl font-bold text-gray-900">Happy Alien</h2>
        <p class="mt-2 text-gray-600">
          {{ showSignup ? 'Create your free account' : 'Sign in to your account' }}
        </p>

        <!-- Toggle Tabs -->
        <div class="mt-6 flex justify-center">
          <div class="rounded-2xl p-1 flex bg-gray-100">
            <button
              @click="showSignup = false"
              class="px-6 py-2 rounded-xl font-medium transition-all duration-300"
              :class="!showSignup ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'"
            >
              Sign In
            </button>
            <button
              @click="showSignup = true"
              class="px-6 py-2 rounded-xl font-medium transition-all duration-300"
              :class="showSignup ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      <!-- Login Form -->
      <div class="card" v-if="!showSignup">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Error Message -->
          <div v-if="error" class="rounded-xl p-4 border bg-red-50 border-red-200">
            <div class="flex">
              <PhWarning class="w-5 h-5 flex-shrink-0 text-red-400" weight="fill" />
              <div class="ml-3">
                <p class="text-sm text-red-800">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium mb-1 text-gray-700">
              Email address
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="input"
              placeholder="Enter your email"
              :disabled="isLoading"
            />
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium mb-1 text-gray-700">
              Password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              class="input"
              placeholder="Enter your password"
              :disabled="isLoading"
            />
          </div>

          <!-- Remember me -->
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                v-model="rememberMe"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label for="remember-me" class="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <router-link to="/forgot-password" class="text-sm font-medium text-primary hover:text-primary/80">
              Forgot password?
            </router-link>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full btn-primary flex items-center justify-center"
          >
            <div
              v-if="isLoading"
              class="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
            ></div>
            {{ isLoading ? "Signing in..." : "Sign in" }}
          </button>
        </form>
      </div>

      <!-- Signup Form Inline -->
      <div class="card" v-if="showSignup">
        <SignupModal
          :isModal="false"
          @close="showSignup = false"
          @success="handleSignupSuccess"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { PhWarning } from "@phosphor-icons/vue";
import { useAuthStore } from "../stores/auth";
import { useNotificationStore } from "../stores/notifications";
import SignupModal from "../components/SignupModal.vue";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();

// Form state
const email = ref("");
const password = ref("");
const rememberMe = ref(false);
const showSignup = ref(false);
const isLoading = ref(false);
const error = ref("");

// Methods
async function handleSubmit() {
  if (!email.value || !password.value) {
    error.value = "Please fill in all fields";
    return;
  }

  isLoading.value = true;
  error.value = "";

  try {
    const result = await authStore.login(email.value, password.value);

    if (result.success) {
      notificationStore.success(
        "Welcome back!",
        "You have been signed in successfully."
      );

      // Initialize the sparks store immediately after login
      const { useSparksStore } = await import('../stores/sparks');
      const sparksStore = useSparksStore();

      if (!sparksStore.isInitialized && authStore.accessToken) {
        const apiUrl = (import.meta as any).env.VITE_API_URL || "http://localhost:3001";

        // Initialize sparks store with API URL and token
        sparksStore.initializeWithAPI(apiUrl, authStore.accessToken);

        // Load initial data
        try {
          await sparksStore.refreshDashboard();
          sparksStore.startAutoRefresh();
        } catch (error) {
          console.error("Failed to load initial data:", error);
        }
      }

      // Wait for auth state to update before redirecting
      await new Promise(resolve => setTimeout(resolve, 100));

      // Handle redirect properly
      const redirectParam = route.query.redirect as string;

      if (redirectParam) {
        // If it's an external Happy Alien URL, use window.location
        try {
          const redirectUrl = new URL(redirectParam);
          if (redirectUrl.hostname.includes('happyalien.ai') ||
              redirectUrl.hostname === 'localhost') {
            console.log("Login successful, redirecting to external:", redirectParam);
            window.location.href = redirectParam;
            return;
          }
        } catch {
          // Not a valid URL, treat as internal route
          console.log("Login successful, redirecting to internal:", redirectParam);
          router.push(redirectParam);
        }
      } else {
        // Default to dashboard home
        console.log("Login successful, redirecting to home");
        router.push("/");
      }
    } else {
      error.value = result.error || "Login failed";
    }
  } catch (err) {
    error.value = "An unexpected error occurred";
    console.error("Login error:", err);
  } finally {
    isLoading.value = false;
  }
}

function handleSignupSuccess() {
  showSignup.value = false;
  notificationStore.success(
    "Account created!",
    "Please check your email to verify your account."
  );
}

// Check if already logged in and handle mode
onMounted(() => {
  // Check if we should show signup based on query param
  if (route.query.mode === 'signup') {
    showSignup.value = true;
  }

  if (authStore.isAuthenticated) {
    router.push("/");
  }
});
</script>
